import React from "react";
import HttpsRedirect from "react-https-redirect";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";
import { ApolloProvider } from "@apollo/client";
import TagManager from "react-gtm-module";

import "./index.css";
import App from "./App.jsx";
import * as serviceWorker from "./serviceWorker";
import theme from "./theme";
import "./i18n";
import client from "./apollo";
import "./numeralConfig";
import { LoadingProvider } from "./contexts/LoadingContext";
import { STATIC_PUBLIC_ROUTES } from "./publicRouteConfig";

const tagManagerArgs = {
  gtmId: "GTM-MJQQQT7",
};

const initializeTagManager = () => {
  if (typeof window === "undefined") {
    return;
  }
  if (window.__energeaGtmInitialized) {
    return;
  }
  window.__energeaGtmInitialized = true;
  TagManager.initialize(tagManagerArgs);
};

if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  const resizeObserverErrorRegex =
    /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)\.?$/;

  window.addEventListener("error", (event) => {
    if (resizeObserverErrorRegex.test(event?.message || "")) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  });
}
if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
  const deferredThirdPartyPaths = new Set(
    STATIC_PUBLIC_ROUTES.filter(
      ({ prerenderKind }) =>
        prerenderKind === "home" || prerenderKind === "paid-media-landing"
    ).map(({ path }) => path)
  );

  const queueWhenIdle = (callback, timeoutMs, fallbackDelayMs) => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(callback, { timeout: timeoutMs });
      return;
    }
    window.setTimeout(callback, fallbackDelayMs);
  };

  const initializeDeferredThirdParties = () => {
    queueWhenIdle(() => initializeTagManager(), 5000, 2200);
    if (typeof window.loadFontAwesomeKit === "function") {
      queueWhenIdle(
        () => window.loadFontAwesomeKit().catch(() => {}),
        8000,
        3500
      );
    }
  };

  const scheduleThirdPartyLoad = () => {
    const shouldDeferForLandingExperience = deferredThirdPartyPaths.has(
      window.location.pathname
    );
    const isMobileViewport =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 767px)").matches;

    if (shouldDeferForLandingExperience) {
      const interactionEvents = [
        "pointerdown",
        "touchstart",
        "keydown",
        "click",
      ];
      let fallbackTimer = null;

      const cleanup = () => {
        interactionEvents.forEach((eventName) => {
          window.removeEventListener(eventName, onFirstInteraction);
        });
        if (fallbackTimer) {
          window.clearTimeout(fallbackTimer);
        }
      };

      const onFirstInteraction = () => {
        cleanup();
        initializeDeferredThirdParties();
      };

      interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, onFirstInteraction, {
          once: true,
          passive: true,
        });
      });

      fallbackTimer = window.setTimeout(
        () => {
          cleanup();
          initializeDeferredThirdParties();
        },
        isMobileViewport ? 12000 : 9000
      );
      return;
    }

    initializeDeferredThirdParties();
  };

  if (document.readyState === "complete") {
    scheduleThirdPartyLoad();
  } else {
    window.addEventListener("load", scheduleThirdPartyLoad, { once: true });
  }
} else if (typeof window !== "undefined") {
  const loadFontAwesomeInNonProd = () => {
    if (typeof window.loadFontAwesomeKit === "function") {
      window.loadFontAwesomeKit().catch(() => {});
    }
  };
  if (document.readyState === "complete") {
    loadFontAwesomeInNonProd();
  } else {
    window.addEventListener("load", loadFontAwesomeInNonProd, { once: true });
  }
}

// Fix for malformed URLs where WhatsApp stripped the "?" from query strings.
// Redirects URLs like /download=1&fatura=123&key=abc to /?download=1&fatura=123&key=abc
// This must happen BEFORE React Router processes the path.
// This should be safe to remove any time after April 1, 2025 (probably sooner than that unless someone tries to view an old invoice)
const pathname = window.location.pathname;
if (pathname.match(/^\/(download|signtoa|signpa)=/)) {
  const queryString = pathname.substring(1);
  const redirectUrl = `/?${queryString}`;

  // Use window.location.assign to redirect with page reload
  // This ensures React picks up the query parameters
  window.location.assign(redirectUrl);
}

const root = createRoot(document.getElementById("root"));

root.render(
  <HttpsRedirect>
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <LoadingProvider>
        <Router>
          <ApolloProvider client={client}>
            <App />
          </ApolloProvider>
        </Router>
      </LoadingProvider>
    </MuiThemeProvider>
  </HttpsRedirect>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
